import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Chip,
  Badge,
  Card,
  alpha,
} from '@mui/material';
import { People } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { RowListSkeleton } from '../skeletons';

interface CommunityMember {
  id: string;
  user: {
    id: string;
    username: string;
    profilePicture?: string;
    createdAt: string;
  };
  role: 'owner' | 'moderator' | 'member';
  joinedAt: string;
  notificationsEnabled: boolean;
}

interface MembersTabProps {
  members: CommunityMember[];
  membersLoading: boolean;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  glassEffect: any;
}

const MembersTab: React.FC<MembersTabProps> = ({
  members,
  membersLoading,
  isDark,
  goldColor,
  deepGreen,
  glassEffect,
}) => {
  const navigate = useNavigate(); // Initialize navigate

  // Function to handle user profile click
  const handleUserProfileClick = (username: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent event bubbling
    }
    if (username) {
      navigate(`/${username}`);
    }
  };

  return (
    <Box>
      {membersLoading ? (
        <RowListSkeleton count={8} label="Loading members" />
      ) : (
        <Card sx={{ 
          p: { xs: 2, md: 4 },
          borderRadius: '20px',
          ...glassEffect,
          border: `1px solid ${alpha(goldColor, 0.2)}`,
          minHeight: { xs: 'auto', md: '400px' }
        }}>
          <Typography variant="h5" sx={{ 
            mb: 3, 
            fontWeight: 800, 
            color: isDark ? '#fff' : deepGreen,
            fontSize: { xs: '1.2rem', md: '1.5rem' }
          }}>
            Community Members ({members.length})
          </Typography>
          
          {members.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <People sx={{ fontSize: { xs: 40, md: 60 }, color: goldColor, mb: 2, opacity: 0.5 }} />
              <Typography sx={{ 
                color: isDark ? alpha('#fff', 0.6) : alpha(deepGreen, 0.6),
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}>
                No members found
              </Typography>
            </Box>
          ) : (
            <List>
              {members.map((member) => (
                <ListItem
                  key={member.id}
                  sx={{
                    borderRadius: '12px',
                    mb: 1,
                    p: { xs: 1, md: 2 },
                    cursor: 'pointer', // Add cursor pointer
                    ...glassEffect,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 30px ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    },
                    transition: 'all 0.2s'
                  }}
                  onClick={(e) => handleUserProfileClick(member.user.username, e)}
                >
                  <ListItemAvatar>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserProfileClick(member.user.username);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          member.role === 'owner' ? (
                            <Box sx={{ 
                              width: { xs: 16, md: 20 }, 
                              height: { xs: 16, md: 20 }, 
                              borderRadius: '50%',
                              bgcolor: goldColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `2px solid ${isDark ? deepGreen : '#fff'}`,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                              transition: 'transform 0.2s ease',
                            }}>
                              <Typography variant="caption" sx={{ 
                                color: deepGreen, 
                                fontWeight: 800, 
                                fontSize: { xs: '8px', md: '10px' } 
                              }}>
                                O
                              </Typography>
                            </Box>
                          ) : member.role === 'moderator' ? (
                            <Box sx={{ 
                              width: { xs: 16, md: 20 }, 
                              height: { xs: 16, md: 20 }, 
                              borderRadius: '50%',
                              bgcolor: '#2196F3',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `2px solid ${isDark ? deepGreen : '#fff'}`,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                              transition: 'transform 0.2s ease',
                            }}>
                              <Typography variant="caption" sx={{ 
                                color: '#fff', 
                                fontWeight: 800, 
                                fontSize: { xs: '8px', md: '10px' } 
                              }}>
                                M
                              </Typography>
                            </Box>
                          ) : null
                        }
                      >
                        <Avatar
                          src={member.user.profilePicture}
                          sx={{ 
                            width: { xs: 40, md: 48 }, 
                            height: { xs: 40, md: 48 },
                            border: `2px solid ${alpha(goldColor, 0.3)}`,
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          {member.user.username?.charAt(0).toUpperCase()}
                        </Avatar>
                      </Badge>
                    </Box>
                  </ListItemAvatar>
                  <Box 
                    sx={{ 
                      flex: 1, 
                      minWidth: 0,
                      cursor: 'pointer',
                      '&:hover .user-name': {
                        color: goldColor,
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUserProfileClick(member.user.username);
                    }}
                  >
                    <Typography 
                      className="user-name"
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: isDark ? '#fff' : deepGreen,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {member.user.username}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                      display: 'block',
                      fontSize: { xs: '0.7rem', md: '0.8rem' }
                    }}>
                      Joined {new Date(member.joinedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Chip
                    label={member.role}
                    size="small"
                    sx={{
                      bgcolor: 
                        member.role === 'owner' ? alpha(goldColor, 0.2) :
                        member.role === 'moderator' ? alpha('#2196F3', 0.2) :
                        alpha('#4CAF50', 0.2),
                      color: 
                        member.role === 'owner' ? goldColor :
                        member.role === 'moderator' ? '#2196F3' :
                        '#4CAF50',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      fontSize: { xs: '0.7rem', md: '0.75rem' },
                      height: { xs: 24, md: 28 },
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUserProfileClick(member.user.username);
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Card>
      )}
    </Box>
  );
};

export default MembersTab;